import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Papa from 'papaparse';
import Encoding from 'encoding-japanese';
// xlsx は Excel 出力時にのみ動的 import する（メインバンドルに含めないため）
import type * as XLSXTypes from 'xlsx';
import { PatternType, ItemListType, ExportColumn } from '../../types/pattern';
import { InputFileType } from '../../types/input';
import { getUser } from '../../api/firebase/firestore/user/read';
import {
  processingStatusLabel,
  fixedFieldIdMap,
  exportFileType,
  exportEncoding,
  exportQuoteMode,
  FIXED_FIELD_ID_PREFIX,
  CHILD_FIELD_ID_SEPARATOR,
} from '../../constants/enum';

dayjs.extend(customParseFormat);

/**
 * resultsデータからファイル名を取得するヘルパー関数
 * originalFiles配列から最初のファイル名を取得、なければ直接file_nameを参照
 */
const getFileName = (data: Record<string, unknown>): string => {
  // originalFiles（camelCase）から取得
  const originalFiles = data.originalFiles as InputFileType[] | undefined;
  if (originalFiles && originalFiles.length > 0 && originalFiles[0].file_name) {
    return originalFiles[0].file_name;
  }
  // original_files（snake_case）から取得（Firestoreからの直接データの場合）
  const originalFilesSnake = data.original_files as InputFileType[] | undefined;
  if (originalFilesSnake && originalFilesSnake.length > 0 && originalFilesSnake[0].file_name) {
    return originalFilesSnake[0].file_name;
  }
  // 直接file_nameがあればそれを使用（後方互換性）
  if (data.file_name && typeof data.file_name === 'string') {
    return data.file_name;
  }
  return '-';
};

// 日付文字列をパースする際に試行する入力形式のリスト
const DATE_INPUT_FORMATS = [
  'YYYY/MM/DD',
  'YYYY-MM-DD',
  'YYYY.MM.DD',
  'YYYYMMDD',
  'YYYY年MM月DD日',
  'YYYY年M月D日',
  'YYYY/M/D',
  'YYYY-M-D',
  'YY/MM/DD',
  'YY-MM-DD',
  'YYMMDD',
  'MM/DD/YYYY',
  'DD/MM/YYYY',
];

/**
 * 日付として解釈できる先頭部分を取り出す（#3144）。
 *
 * 出力形式に不正な値（例: 'YYYY-MM-DD HH:MM:SS'）が指定されたまま OCR された結果、
 * 'SS' がトークンとして解釈されず `2026-03-03 00:03:SS` のような
 * 「日付部分は正しいが末尾が壊れている」文字列が保存されているデータが存在する。
 * こうした値は通常のパースをすべて失敗するため、日付部分だけを救済して変換できるようにする。
 *
 * 区切り文字は DATE_INPUT_FORMATS で扱う `/ - . 年月日` を許容する。
 */
const extractLeadingDatePart = (value: string): string | null => {
  const matched = value
    .trim()
    .match(/^(\d{4}[/\-.]\d{1,2}[/\-.]\d{1,2}|\d{4}年\d{1,2}月\d{1,2}日|\d{8})/);
  return matched ? matched[1] : null;
};

/**
 * 数字を1文字も含まない値の判定（#3275）。
 * DATE_INPUT_FORMATS はすべて数字を含む形式なので、
 * 数字が無い文字列は厳密パースを13回試しても必ず全部失敗する。
 * 'March 3, 2026' のような値は後段の緩いパースが拾うため、結果は変わらない。
 */
const HAS_DIGIT = /\d/;

/**
 * パース結果のキャッシュ（#3275）。
 *
 * 明細122行×8ページ規模の帳票で、この関数が全セル分呼ばれる。
 * 1回の呼び出しで最大27回（13形式 × 2 + 緩いパース）の dayjs 変換が走るため、
 * 本番実測ではここが表示処理の約48%を占めていた。
 * 明細の日付は同じ値の繰り返しが多く、キャッシュがよく効く。
 *
 * 同じ入力には同じ結果を返すだけなので、パース結果そのものは変わらない。
 * dayjs オブジェクトはイミュータブル（add 等は新しい実体を返す）なので、
 * 使い回しても呼び出し側で書き換わることはない。
 */
const parseDateCache = new Map<string, dayjs.Dayjs | null>();
const PARSE_DATE_CACHE_MAX = 2000;

/** キャッシュを介さない本体。挙動は従来のまま。 */
const parseDateStringUncached = (value: string): dayjs.Dayjs | null => {
  // 数字が無ければ厳密パースは必ず全滅するので飛ばす
  if (HAS_DIGIT.test(value)) {
    for (const inputFormat of DATE_INPUT_FORMATS) {
      const parsed = dayjs(value, inputFormat, true);
      if (parsed.isValid()) return parsed;
    }
  }
  const loose = dayjs(value);
  if (loose.isValid()) return loose;

  // 末尾が壊れている文字列（例: '2026-03-03 00:03:SS'）を日付部分だけ救済する
  const datePart = extractLeadingDatePart(value);
  if (datePart) {
    for (const inputFormat of DATE_INPUT_FORMATS) {
      const parsed = dayjs(datePart, inputFormat, true);
      if (parsed.isValid()) return parsed;
    }
    const parsedLoose = dayjs(datePart);
    if (parsedLoose.isValid()) return parsedLoose;
  }
  return null;
};

/**
 * 日付文字列を dayjs に変換する（#3144）。
 * 厳密パース → 緩いパース → 先頭の日付部分だけを救済、の順に試す。
 * いずれも失敗した場合は null を返す（呼び出し側で元の値を維持する）。
 *
 * 結果はキャッシュする（#3275）。試す順序は従来どおりで、
 * 'MM/DD/YYYY' と 'DD/MM/YYYY' のどちらにも当てはまる値（例: '10/04/2026'）が
 * リスト順で先に来る形式に解決される挙動も変えていない。
 */
export const parseDateStringSafely = (value: string): dayjs.Dayjs | null => {
  const cached = parseDateCache.get(value);
  // null もパース失敗として記録済みなので、has ではなく undefined で判定する
  if (cached !== undefined) return cached;

  const parsed = parseDateStringUncached(value);
  // 際限なく増えないよう、上限に達したら捨てて作り直す
  if (parseDateCache.size >= PARSE_DATE_CACHE_MAX) parseDateCache.clear();
  parseDateCache.set(value, parsed);
  return parsed;
};

/**
 * パターンのitem_listから日付フィールドのID→出力形式マップを構築
 * キーは親ID（例: "1"）または子ID（例: "2-1"の"子ID"部分ではなく完全ID）
 */
const buildDateFormatMap = (itemList: ItemListType[]): Map<string, string> => {
  const map = new Map<string, string>();
  itemList.forEach(item => {
    if (item.type === 'date' && item.is_active !== false && item.outputFormat) {
      map.set(item.id.toString(), item.outputFormat);
    }
    if (item.children) {
      item.children.forEach(child => {
        if (child.type === 'date' && child.is_active !== false && child.outputFormat) {
          map.set(child.id.toString(), child.outputFormat);
        }
      });
    }
  });
  return map;
};

/**
 * 値を日付出力形式に従って文字列に変換
 * Timestamp/Date → dayjs.format、文字列 → 再パースしてformat、その他 → String()
 */
export const formatDateValue = (value: unknown, outputFormat: string): string => {
  if (value === null || value === undefined) return '';

  // Firestore Timestamp（{seconds, nanoseconds}）
  if (typeof value === 'object' && 'seconds' in value) {
    const ts = value as { seconds: number };
    return dayjs(ts.seconds * 1000).format(outputFormat);
  }

  // Date オブジェクト
  if (value instanceof Date) {
    return dayjs(value).format(outputFormat);
  }

  // 文字列の場合：既にフォーマット済みの可能性があるが、再パースして統一
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseDateStringSafely(value);
    if (parsed) {
      return parsed.format(outputFormat);
    }
    // パースできない場合はそのまま返す
    return value;
  }

  return value ? String(value) : '';
};

/**
 * 読取パターン結果データから配列展開済みCSVデータを生成
 */
export const generatePatternExportData = async (
  results: Record<string, unknown>[],
  pattern: PatternType,
  selectedFields: string[],
  selectedFixedFields?: string[],
): Promise<Record<string, string>[]> => {
  const csvData: Record<string, string>[] = [];
  // 日付フィールドのID→出力形式マップ（Timestamp/Date値のフォーマットに使用）
  const dateFormatMap = buildDateFormatMap(pattern.item_list);

  // 値を文字列に変換するヘルパー（日付フィールドの場合はフォーマットを適用）
  const valueToString = (value: unknown, fieldId?: string): string => {
    if (fieldId) {
      const outputFormat = dateFormatMap.get(fieldId);
      if (outputFormat) {
        return formatDateValue(value, outputFormat);
      }
    }
    // Firestore Timestamp型の場合は日付文字列に変換
    if (value !== null && typeof value === 'object' && 'seconds' in value) {
      const ts = value as { seconds: number };
      return dayjs(ts.seconds * 1000).format('YYYY/MM/DD HH:mm');
    }
    // 数値の 0 や false を空文字にしないよう、falsy 一括判定ではなく null/undefined のみ空にする。
    // 固定値(#3073)に 0 を設定するケースで 0 が出力されないため修正した。
    if (value === null || value === undefined) return '';
    return String(value);
  };

  for (let recordIndex = 0; recordIndex < results.length; recordIndex++) {
    const data = results[recordIndex];

    // 固定項目の選択判定（未指定時は全項目出力＝後方互換性）
    const isFixedSelected = (field: string): boolean =>
      !selectedFixedFields || selectedFixedFields.includes(field);

    // 作成者・更新者の名前を取得（選択されている場合のみ）
    const createdUser =
      isFixedSelected('作成者') && data.user_id ? await getUser(data.user_id as string) : null;
    const updatedUser =
      isFixedSelected('更新者') && data.updated_by
        ? await getUser(data.updated_by as string)
        : null;

    // 基本情報（選択された固定項目のみ）
    const baseData: Record<string, string> = {};
    if (isFixedSelected('ファイル名')) {
      baseData['ファイル名'] = getFileName(data);
    }
    if (isFixedSelected('アップロード日時')) {
      baseData['アップロード日時'] = data.created_at
        ? dayjs((data.created_at as { seconds: number }).seconds * 1000).format('YYYY/MM/DD HH:mm')
        : '-';
    }
    if (isFixedSelected('更新日時')) {
      baseData['更新日時'] = data.updated_at
        ? dayjs((data.updated_at as { seconds: number }).seconds * 1000).format('YYYY/MM/DD HH:mm')
        : '-';
    }
    if (isFixedSelected('作成者')) {
      baseData['作成者'] = (createdUser?.name as string) || '-';
    }
    if (isFixedSelected('更新者')) {
      baseData['更新者'] = (updatedUser?.name as string) || '-';
    }

    // 読取パターン項目のデータを取得し、配列情報を管理
    const patternValues: Record<string, string[]> = {};
    const arrayInfo: Array<{ name: string; length: number; startRow: number }> = [];
    let totalRows = 0;

    // 親項目の処理（is_active: falseの項目は除外）
    pattern.item_list
      .filter(item => item.is_active !== false)
      .forEach(item => {
        const isParentSelected = selectedFields.includes(item.id.toString());
        const selectedChildren = selectedFields.filter(field => field.startsWith(`${item.id}-`));

        if (isParentSelected || selectedChildren.length > 0) {
          // データ構造に応じて値を取得（IDベースで検索し、見つからなければ項目名で検索）
          const resultDataObj = data.result_data as Record<string, unknown>;
          // IDは文字列として検索（Firestoreのキーは文字列）
          const resultData =
            resultDataObj?.[item.id.toString()] || resultDataObj?.[item.parentValue];
          let resultValue: unknown;

          // {value: actualValue, bounding_boxes: [...]}の形式の場合
          if (resultData && typeof resultData === 'object' && 'value' in resultData) {
            resultValue = (resultData as { value: unknown }).value;
          }
          // 直接値（配列など）の場合
          else {
            resultValue = resultData;
          }

          if (Array.isArray(resultValue)) {
            // 配列情報を記録
            arrayInfo.push({
              name: item.parentValue,
              length: resultValue.length,
              startRow: totalRows,
            });
            totalRows += resultValue.length;

            // 親項目（配列そのもの）は列だけを出力し値は持たせない（#3159）
            if (isParentSelected) {
              patternValues[item.parentValue] = [];
            }

            // 子項目が選択されている場合（item.children の順序でループしてダイアログでの並び替え順を反映）
            item.children?.forEach(child => {
              if (child.is_active === false) return;
              const childField = `${item.id}-${child.id}`;
              if (!selectedFields.includes(childField)) return;

              const fieldName = `${item.parentValue}.${child.childValue}`;

              patternValues[fieldName] = (resultValue as unknown[]).map((val: unknown) => {
                // 配列の各要素がオブジェクトの場合
                if (val && typeof val === 'object') {
                  // IDベースで検索し、見つからなければ項目名で検索（後方互換性）
                  // IDは文字列として検索（Firestoreのキーは文字列）
                  const valObj = val as Record<string, unknown>;
                  const childData = valObj[child.id.toString()] || valObj[child.childValue];
                  // childDataが{value: "actual_value", bounding_boxes: [...]}の形式の場合
                  if (childData && typeof childData === 'object' && 'value' in childData) {
                    const value = (childData as { value: unknown }).value;
                    return valueToString(value, child.id.toString());
                  }
                  // 直接値の場合
                  else {
                    return valueToString(childData, child.id.toString());
                  }
                }
                // 配列の各要素が単純な値の場合（インデックスベース）
                else if (Array.isArray(val)) {
                  const childIndex = parseInt(child.childValue) || 0;
                  const value = val[childIndex];
                  return valueToString(value, child.id.toString());
                }
                // その他の場合（値そのものを使用）
                else {
                  return valueToString(val, child.id.toString());
                }
              });
              // 配列情報は上で記録済み
            });
          } else {
            // 非配列の場合
            if (isParentSelected) {
              patternValues[item.parentValue] = [valueToString(resultValue, item.id.toString())];
            }

            // item.children の順序でループしてダイアログでの並び替え順を反映
            item.children?.forEach(child => {
              if (child.is_active === false) return;
              const childField = `${item.id}-${child.id}`;
              if (!selectedFields.includes(childField)) return;

              if (resultValue && typeof resultValue === 'object') {
                const fieldName = `${item.parentValue}.${child.childValue}`;
                // IDベースで検索し、見つからなければ項目名で検索（後方互換性）
                // IDは文字列として検索（Firestoreのキーは文字列）
                const resultObj = resultValue as Record<string, unknown>;
                const childData = resultObj[child.id.toString()] || resultObj[child.childValue];
                // childDataが{value: ...}形式の場合は値を取り出す
                let finalValue: unknown = childData;
                if (childData && typeof childData === 'object' && 'value' in childData) {
                  finalValue = (childData as { value: unknown }).value;
                }
                patternValues[fieldName] = [valueToString(finalValue, child.id.toString())];
              }
            });
          }
        }
      });

    // 配列がない場合は最低1行必要
    if (totalRows === 0) {
      totalRows = 1;
    }

    // 各配列の行を順次生成
    for (let globalRowIndex = 0; globalRowIndex < totalRows; globalRowIndex++) {
      const rowData: Record<string, string> = {
        ...(isFixedSelected('レコードの開始行')
          ? { レコードの開始行: globalRowIndex === 0 ? '*' : '' }
          : {}),
        ...baseData, // 基本情報は全行で同じ値
      };

      // どの配列のどの行かを判定
      let currentArrayInfo: { name: string; length: number; startRow: number } | null = null;
      let arrayLocalIndex = -1;

      for (const arrayInfoItem of arrayInfo) {
        if (
          globalRowIndex >= arrayInfoItem.startRow &&
          globalRowIndex < arrayInfoItem.startRow + arrayInfoItem.length
        ) {
          currentArrayInfo = arrayInfoItem;
          arrayLocalIndex = globalRowIndex - arrayInfoItem.startRow;
          break;
        }
      }

      // 各読取パターン項目の値を設定
      Object.keys(patternValues).forEach(fieldName => {
        const values = patternValues[fieldName] || [];

        // 配列項目かどうかを判定
        const arrayInfoForField = arrayInfo.find(ai => ai.name === fieldName.split('.')[0]);

        if (arrayInfoForField) {
          // 現在の行がこの配列の担当範囲かチェック
          if (currentArrayInfo && currentArrayInfo.name === arrayInfoForField.name) {
            // 親要素（配列そのもの）は列（ヘッダー）だけを残し、
            // 行番号にあたるインデックス番号は出力しない（#3159）
            if (!fieldName.includes('.')) {
              rowData[fieldName] = '';
            }
            // 子要素の場合は実際の値を設定
            else {
              rowData[fieldName] = values[arrayLocalIndex] || '';
            }
          } else {
            // この配列の担当範囲外なので空欄
            rowData[fieldName] = '';
          }
        }
        // 非配列項目の場合は常に同じ値を設定
        else {
          rowData[fieldName] = values[0] || '';
        }
      });

      csvData.push(rowData);
    }
  }

  return csvData;
};

/**
 * 列定義を作るヘルパー（#3158）
 * カスタムの列名が設定されていればそれを列名に使い、内部キーは出力データのキーのまま保つ。
 *
 * @param key 出力データを参照するための内部キー
 * @param defaultLabel カスタムの列名が未設定のときに出力する列名
 */
const toExportColumnWithCustomLabel = (
  key: string,
  defaultLabel: string,
  fieldId: string,
  customHeaders?: Record<string, string>,
): ExportColumn => {
  const customLabel = customHeaders?.[fieldId]?.trim();
  return { key, label: customLabel || defaultLabel };
};

/**
 * パターン項目の列定義を生成するヘルパー
 */
const getPatternItemColumns = (
  item: PatternType['item_list'][0],
  selectedFields: string[],
  customHeaders?: Record<string, string>,
): ExportColumn[] => {
  const columns: ExportColumn[] = [];
  const isParentSelected = selectedFields.includes(item.id.toString());

  if (isParentSelected) {
    columns.push(
      toExportColumnWithCustomLabel(
        item.parentValue,
        item.parentValue,
        item.id.toString(),
        customHeaders,
      ),
    );
  }

  // item.children の順序でループしてダイアログでの並び替え順を反映
  item.children?.forEach(child => {
    const childField = `${item.id}${CHILD_FIELD_ID_SEPARATOR}${child.id}`;
    if (!selectedFields.includes(childField)) return;
    columns.push(
      toExportColumnWithCustomLabel(
        `${item.parentValue}.${child.childValue}`,
        child.childValue,
        childField,
        customHeaders,
      ),
    );
  });

  return columns;
};

/**
 * 出力する列定義（内部キー + 列名）を生成（#3159）
 *
 * 出力データのキーと列名を分離して返すため、列名だけを差し替える拡張ができる。
 */
export const generateExportColumns = (
  pattern: PatternType,
  selectedFields: string[],
  selectedFixedFields?: string[],
  sortedUnifiedOrder?: string[],
  customHeaders?: Record<string, string>,
): ExportColumn[] => {
  const allFixedHeaders = [
    'レコードの開始行',
    'ファイル名',
    'アップロード日時',
    '更新日時',
    '作成者',
    '更新者',
  ];

  // 統合リスト形式（sorted_item_orderにfixed_が含まれる）の場合
  if (sortedUnifiedOrder && sortedUnifiedOrder.some(id => id.startsWith(FIXED_FIELD_ID_PREFIX))) {
    const columns: ExportColumn[] = [];
    // 子項目IDを持たない親（#3159 以前の保存データ）は、親の直後に子を並べて従来の順序を保つ
    const hasOrderedChild = (parentId: string): boolean =>
      sortedUnifiedOrder.some(id => id.startsWith(`${parentId}${CHILD_FIELD_ID_SEPARATOR}`));

    sortedUnifiedOrder.forEach(orderId => {
      if (orderId.startsWith(FIXED_FIELD_ID_PREFIX)) {
        // 固定項目: 選択されていれば追加
        const label = fixedFieldIdMap[orderId];
        if (label && (!selectedFixedFields || selectedFixedFields.includes(label))) {
          columns.push(toExportColumnWithCustomLabel(label, label, orderId, customHeaders));
        }
        return;
      }

      const separatorIndex = orderId.indexOf(CHILD_FIELD_ID_SEPARATOR);
      if (separatorIndex >= 0) {
        // 子項目（#3159: 親から独立して並び替えられる）
        const parentId = orderId.slice(0, separatorIndex);
        const childId = orderId.slice(separatorIndex + 1);
        const item = pattern.item_list.find(i => i.id.toString() === parentId);
        const child = item?.children?.find(c => c.id.toString() === childId);
        if (item && child && selectedFields.includes(orderId)) {
          columns.push(
            toExportColumnWithCustomLabel(
              `${item.parentValue}.${child.childValue}`,
              child.childValue,
              orderId,
              customHeaders,
            ),
          );
        }
        return;
      }

      // 親項目
      const item = pattern.item_list.find(i => i.id.toString() === orderId);
      if (!item) return;
      if (selectedFields.includes(orderId)) {
        columns.push(
          toExportColumnWithCustomLabel(item.parentValue, item.parentValue, orderId, customHeaders),
        );
      }
      if (!hasOrderedChild(orderId)) {
        item.children?.forEach(child => {
          const childField = `${item.id}${CHILD_FIELD_ID_SEPARATOR}${child.id}`;
          if (!selectedFields.includes(childField)) return;
          columns.push(
            toExportColumnWithCustomLabel(
              `${item.parentValue}.${child.childValue}`,
              child.childValue,
              childField,
              customHeaders,
            ),
          );
        });
      }
    });

    return columns;
  }

  // レガシー形式: 固定項目 → 読取項目の順
  const fixedIdByLabel = new Map(Object.entries(fixedFieldIdMap).map(([id, label]) => [label, id]));
  const fixedColumns = (
    selectedFixedFields
      ? allFixedHeaders.filter(h => selectedFixedFields.includes(h))
      : allFixedHeaders
  ).map(label =>
    toExportColumnWithCustomLabel(label, label, fixedIdByLabel.get(label) ?? label, customHeaders),
  );

  const selectedPatternColumns: ExportColumn[] = [];
  pattern.item_list.forEach(item => {
    selectedPatternColumns.push(...getPatternItemColumns(item, selectedFields, customHeaders));
  });

  return [...fixedColumns, ...selectedPatternColumns];
};

/**
 * ファイル名を生成
 */
export const generateFileName = (
  pattern: PatternType,
  selectedTab: number,
  fileType: 'excel' | 'csv',
  encoding?: string,
): string => {
  const formattedDate = dayjs().format('YYYYMMDDHHmmss');
  // 新しいフィールド名を優先、なければ後方互換性のため古いフィールド名を使用
  const patternName = pattern.pattern_name.replace(/[\\/:*?"<>|]/g, '_');
  // 「未処理」「確認済」「出力済」いずれのタブでもラベルを付ける
  const statusLabel: string =
    processingStatusLabel[selectedTab as keyof typeof processingStatusLabel] ?? '';
  // ラベルを持たないタブでも区切り文字が連続しないようにする
  const baseName = [patternName, statusLabel, formattedDate].filter(Boolean).join('_');

  if (fileType === 'excel') {
    return `${baseName}.xlsx`;
  } else {
    const encodingSuffix = encoding === 'shift-jis' ? '_sjis' : '_utf8';
    return `${baseName}${encodingSuffix}.csv`;
  }
};

/**
 * 列定義の受け取り形式（#3159）
 * 文字列を渡した場合は、内部キーと列名が同一の列として扱う。
 */
export type ExportColumnInput = string | ExportColumn;

/** CSV出力時の引用符の付け方（#3160） */
export type ExportQuoteMode = (typeof exportQuoteMode)[keyof typeof exportQuoteMode];

/**
 * 文字列と列定義が混在した入力を列定義へ正規化する
 */
const normalizeExportColumns = (columns: ExportColumnInput[]): ExportColumn[] =>
  columns.map(column => (typeof column === 'string' ? { key: column, label: column } : column));

/**
 * 出力データを列定義の順に並べた二次元配列へ変換する（#3159）
 *
 * 出力データのキー（column.key）で値を引くため、列名（column.label）を
 * 自由に差し替えてもデータとの対応が崩れない。
 */
const buildExportRows = (data: Record<string, string>[], columns: ExportColumn[]): string[][] =>
  data.map(row => columns.map(column => row[column.key] ?? ''));

// エクセルファイル（.xlsx）の MIME タイプ
const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * 出力データからエクセルのワークブックを生成する
 */
const buildExportWorkbook = async (
  data: Record<string, string>[],
  columns: ExportColumn[],
): Promise<XLSXTypes.WorkBook> => {
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.aoa_to_sheet([
    columns.map(column => column.label),
    ...buildExportRows(data, columns),
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'データ');
  return workbook;
};

// CSVインジェクション（数式インジェクション）対策のパターン定義
// 先頭が = @ タブ CR のセルは Excel 等で数式として解釈され得る
const FORMULA_TRIGGER_PATTERN = /^[=@\t\r]/;
// + - 始まりも数式になり得るが、負数などの数値表現は無害なので除外する
const SIGN_PREFIX_PATTERN = /^[+-]/;
const NUMERIC_VALUE_PATTERN = /^[+-]?[0-9][0-9.,]*$/;

/**
 * Excel 等で数式として解釈され得るセル値の先頭にシングルクォートを付与して無効化する。
 * User-Agent などユーザー制御下の値をCSV出力しても数式が実行されないようにする。
 */
const sanitizeCsvCell = (value: string): string => {
  if (FORMULA_TRIGGER_PATTERN.test(value)) return `'${value}`;
  if (SIGN_PREFIX_PATTERN.test(value) && !NUMERIC_VALUE_PATTERN.test(value)) return `'${value}`;
  return value;
};

/**
 * 出力データからCSV文字列を生成する
 *
 * @param sanitizeFormula 数式として解釈され得るセルを無効化するか
 * @param quoteMode 引用符の付け方（#3160）。all のときはすべての値を引用符で囲む
 */
const buildCsvString = (
  data: Record<string, string>[],
  columns: ExportColumn[],
  sanitizeFormula: boolean,
  quoteMode?: ExportQuoteMode,
): string => {
  const rows = buildExportRows(data, columns);
  return Papa.unparse(
    {
      fields: columns.map(column => column.label),
      data: sanitizeFormula ? rows.map(row => row.map(sanitizeCsvCell)) : rows,
    },
    // minimal（既定）でも、区切り文字・改行・引用符を含む値は papaparse が自動で囲む
    { quotes: quoteMode === exportQuoteMode.all },
  );
};

/**
 * CSV文字列を指定の文字コードでエンコードして Blob 化する
 */
const buildCsvBlob = (csv: string, encoding: string): Blob => {
  if (encoding === exportEncoding.shiftJis) {
    // Shift-JISエンコード
    const sjisArray = Encoding.convert(Encoding.stringToCode(csv), 'SJIS', 'UNICODE');
    return new Blob([new Uint8Array(sjisArray)], { type: 'text/csv;charset=shift_jis' });
  }
  // UTF-8エンコード（BOM付き）
  const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
  return new Blob([bom, new TextEncoder().encode(csv)], { type: 'text/csv;charset=utf-8' });
};

/**
 * 出力ファイルの Blob を生成する（#3159）
 *
 * ブラウザへ直接ダウンロードするのではなく、アップロード用に Blob が
 * 必要な経路（読取パターンのファイル出力）から使う。
 *
 * @param options.sanitizeFormula 数式として解釈され得るセルを無効化するか（既定: false）
 */
export const buildExportBlob = async (
  data: Record<string, string>[],
  columns: ExportColumnInput[],
  fileType: (typeof exportFileType)[keyof typeof exportFileType],
  encoding: string,
  options?: { sanitizeFormula?: boolean; quoteMode?: ExportQuoteMode },
): Promise<Blob> => {
  const normalizedColumns = normalizeExportColumns(columns);

  if (fileType === exportFileType.excel) {
    const XLSX = await import('xlsx');
    const excelBuffer = XLSX.write(await buildExportWorkbook(data, normalizedColumns), {
      bookType: 'xlsx',
      type: 'array',
    });
    return new Blob([excelBuffer], { type: EXCEL_MIME_TYPE });
  }

  return buildCsvBlob(
    buildCsvString(data, normalizedColumns, options?.sanitizeFormula ?? false, options?.quoteMode),
    encoding,
  );
};

/**
 * CSVファイルを生成・ダウンロード
 */
export const downloadCsv = (
  data: Record<string, string>[],
  columns: ExportColumnInput[],
  fileName: string,
  encoding: string,
): void => {
  // 全セルに数式インジェクション対策を適用してから CSV 化する
  const csv = buildCsvString(data, normalizeExportColumns(columns), true);
  const blob = buildCsvBlob(csv, encoding);

  // ダウンロードリンクを作成
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;

  // リンクをクリックしてダウンロード開始
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
